import { divideSafe } from '@utils/calculations';
import { formatNumberFloat } from '@utils/formatNumberFloat';

import { Valuation } from 'models/Valuation';
import { ActionType, getType } from 'typesafe-actions';

import * as debtPricerActions from './actions';
import legsReducer, {
  DebtLegState,
  LegInitialState,
} from './components/LegsReducer';
import tradeSummaryReducer, {
  TradeSummaryInitialState,
  TradeSummaryState,
} from './components/TradeSummaryReducer';

export type DebtPricerActionType = ActionType<typeof debtPricerActions>;

export type DebtPricerState = {
  transaction: {
    debt_id: null | number;
    trade_summary: TradeSummaryState;
    leg: DebtLegState;
    //transaction_metrics: Metric[];
    transaction_valuation: Valuation[];
  };
  buildScheduleLoading: boolean;
  saveTradeLoading: boolean;
  valueTradeLoading: boolean;
  transactionLoading: boolean;
  getTransactionFailed: boolean;
};

const initialState: DebtPricerState = {
  transaction: {
    debt_id: null,
    trade_summary: TradeSummaryInitialState,
    leg: LegInitialState,
    //transaction_metrics: [],
    transaction_valuation: [],
  },
  buildScheduleLoading: false,
  saveTradeLoading: false,
  valueTradeLoading: false,
  transactionLoading: false,
  getTransactionFailed: false,
};

const debtReducer = (
  state: DebtPricerState = initialState,
  action: DebtPricerActionType,
): DebtPricerState => {
  switch (action.type) {
    case getType(debtPricerActions.getTransactionById.request):
      return {
        ...state,
        getTransactionFailed: false,
        transactionLoading: true,
      };
    case getType(debtPricerActions.getTransactionById.success): {
      const newPar = formatNumberFloat(
        divideSafe(
          action.payload.data.leg.leg_terms.price || 0,
          action.payload.data.leg.leg_terms.notional || 0,
        ) * 100,
      );

      return {
        ...state,
        transactionLoading: false,
        getTransactionFailed: false,
        transaction: {
          ...state.transaction,
          debt_id: action.payload.data.debt_id,
          trade_summary: action.payload.data.trade_summary,
          leg: {
            leg_terms: { ...action.payload.data.leg.leg_terms, par: newPar },
            leg_flows: action.payload.data.leg.leg_flows,
          },
        },
      };
    }
    case getType(debtPricerActions.getTransactionById.failure):
      return {
        ...state,
        transactionLoading: false,
        getTransactionFailed: true,
      };

    case getType(debtPricerActions.buildSchedule.request):
      return {
        ...state,
        buildScheduleLoading: true,
      };
    case getType(debtPricerActions.buildSchedule.success):
      return {
        ...state,
        buildScheduleLoading: false,
        transaction: {
          ...state.transaction,
          leg: legsReducer(state.transaction.leg, action),
        },
      };
    case getType(debtPricerActions.buildSchedule.failure):
      return {
        ...state,
        buildScheduleLoading: false,
      };

    case getType(debtPricerActions.saveTrade.request):
      return {
        ...state,
        saveTradeLoading: true,
      };

    case getType(debtPricerActions.saveTrade.success):
      return {
        ...state,
        saveTradeLoading: false,
        transaction: {
          ...state.transaction,
          debt_id: action.payload.debt_id,
        },
      };
    case getType(debtPricerActions.saveTrade.failure):
      return {
        ...state,
        saveTradeLoading: false,
      };

    case getType(debtPricerActions.valueTrade.request):
      return {
        ...state,
        valueTradeLoading: true,
      };
    case getType(debtPricerActions.valueTrade.success):
      return {
        ...state,
        valueTradeLoading: false,
        transaction: {
          ...state.transaction,
          transaction_valuation: action.payload.data.valuation,
          leg: legsReducer(state.transaction.leg, action),
        },
      };
    case getType(debtPricerActions.valueTrade.failure):
      return {
        ...state,
        valueTradeLoading: false,
      };

    case getType(debtPricerActions.resetAndChangeInstrumentType):
      return {
        ...state,
        transaction: {
          ...state.transaction,
          leg: LegInitialState,
        },
      };

    case getType(debtPricerActions.saveCopiedTrade.request):
      return {
        ...state,
        saveTradeLoading: true,
      };
    case getType(debtPricerActions.saveCopiedTrade.success):
      return {
        ...state,
        saveTradeLoading: false,
      };
    case getType(debtPricerActions.saveCopiedTrade.failure):
      return {
        ...state,
        saveTradeLoading: false,
      };

    case getType(debtPricerActions.reset):
      return {
        ...initialState,
      };

    default:
      return {
        ...state,
        transaction: {
          ...state.transaction,
          trade_summary: tradeSummaryReducer(
            state.transaction.trade_summary,
            action,
          ),
          leg: legsReducer(state.transaction.leg, action),
        },
      };
  }
};

export default debtReducer;
